import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  MenuItem,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Checkbox,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import { post, put, get } from "../../../utility/fetch";
import { get as gets } from "../../../utility/fetchWeb";

import notification from "../../../utility/notification";
import { useLocation, useNavigate } from "react-router-dom";
import UploadButton from "../../Layout/UploadButton";

const CreateGroup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isEditing = location.state?.isEditing;
  const groupData = location.state?.groupData;
  const coverPhotoInputRef = useRef(null);
  const [categories, setCategories] = useState([]);

  const [payload, setPayload] = useState({
    name: groupData?.name || "",
    categoryId: groupData?.categoryId || 0,
    description: groupData?.description || "",
    pictureUrl: groupData?.pictureUrl || "",
  });

  const [selectedFile, setSelectedFile] = useState(groupData?.pictureUrl || null);
  const [groupLoading, setGroupLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [mediaFile, setMediaFile] = useState(null);
  const [docUploading, setDocUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [errors, setErrors] = useState({});

  const getCategories = async () => {
    try {
      const response = await gets("/categories");
      if (response?.code === 200) {
        setCategories(response?.data || []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    if (searchQuery.length < 3) {
      setPatients([]);
      setIsLoadingPatients(false);
      return;
    }

    const fetchPatients = async () => {
      setIsLoadingPatients(true);
      try {
        const res = await gets(
          `/Patient/filter-list/PatientName/${searchQuery}/1/10`
        );
        if (res?.statusCode === 200) {
          setPatients(res.data.recordList || []);
        } else {
          setPatients([]);
        }
      } catch (err) {
        console.error('Fetch patients error', err);
        setPatients([]);
      } finally {
        setIsLoadingPatients(false);
      }
    };

    fetchPatients();
  }, [searchQuery]);

  const getEmployees = async () => {
    try {
      const res = await get("/employee/list/1/100");
      if (res?.statusCode === 200) {
        setEmployees(res?.data?.recordList);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      setEmployees([]);
      console.error("Error fetching employees:", error);
    }
  };

  useEffect(() => {
    getCategories();
    // Remove getPatients() call
    getEmployees();
  }, []);


  const handleInputChange = (e) => {
    setPayload({ ...payload, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!payload?.name.trim()) {
      newErrors.name = 'Group name is required';
    }

    if (!payload?.categoryId || payload?.categoryId === 0) {
      newErrors.categoryId = 'Category is required';
    }

    if (!payload?.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!payload?.pictureUrl.trim()) {
      newErrors.pictureUrl = 'Cover photo is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      notification({
        type: "error",
        message: "Please fill in all required fields",
        duration: 3000,
      });
      return;
    }

    setGroupLoading(true);
    try {

      // Then create/update the group with all data
      const groupPayload = {
        name: payload?.name,
        categoryId: payload?.categoryId,
        description: payload?.description,
        pictureUrl: payload?.pictureUrl,
        communityGroupMembers: selectedMembers.map((member) => ({
          patientId: member.isHealthPractitioner ? 0 : member.id,
          employeeId: member.isHealthPractitioner ? member.id : 0,
          isHealthPractitioner: member.isHealthPractitioner,
        })),
      };

      const groupUpdatePayload = {
        name: payload?.name,
        categoryId: payload?.categoryId,
        description: payload?.description,
        pictureUrl: payload?.pictureUrl,
      };

      const res = isEditing
        ? await put(`/communitygroup/${groupData.id}`, groupUpdatePayload)
        : await post("/communitygroup", groupPayload);

      if (res?.statusCode === 200) {
        notification({
          type: "success",
          message: isEditing
            ? "Group updated successfully!"
            : "Group created successfully!",
          duration: 3000,
        });
        navigate(-1);
      }
    } catch (error) {
      console.error(error);
      notification({
        type: "error",
        message: isEditing ? "Failed to update group" : "Failed to create group",
        duration: 3000,
      });
    } finally {
      setGroupLoading(false);
    }
  };

  // First, add the helper functions after your state declarations
  const isHealthPractitioner = (person) => {
    if (person.userRoles) {
      return person.userRoles.some(role =>
        role.role.name.toLowerCase().includes('doctor') ||
        role.role.name.toLowerCase().includes('nurse') ||
        role.role.name.toLowerCase().includes('health')
      );
    }
    return false;
  };

  const getUserTypeDisplay = (person) => {
    if (person.userRoles) {
      const role = person.userRoles.find(role =>
        role.role.name.toLowerCase().includes('doctor') ||
        role.role.name.toLowerCase().includes('nurse') ||
        role.role.name.toLowerCase().includes('health')
      );
      return role ? role.role.name : 'Staff';
    }
    return 'citizen';
  };

  // Update the title and button text based on mode
  return (
    <Container maxWidth="100vw" sx={{ py: 4 }}>
      {/* Add this section for the back button and title */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
        }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            bgcolor: "background.paper",
            boxShadow: 1,
            "&:hover": {
              bgcolor: "grey.100",
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {isEditing ? "Edit Community Group" : "Create New Community Group"}
        </Typography>
      </Box>

      {/* Cover Photo Section */}
      <Paper
        sx={{
          width: "100%",
          height: 250,
          mb: 4,
          position: "relative",
          backgroundImage: `url("${encodeURI(selectedFile || "/placeholder-cover.jpg")}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
            display: "flex",
            gap: 1,
          }}
        >
          <UploadButton
            acceptedFiles="image/*"
            buttonText="Change Cover Photo"
            onUploadSuccess={(response) => {
              if (response?.code === 1) {
                setPayload(prev => ({
                  ...prev,
                  pictureUrl: response.doclink
                }));

                setSelectedFile(response.doclink);
              }
            }}
            onUploadError={() => {
              notification({ type: 'error', message: 'Cover photo upload failed' });
            }}
            buttonProps={{
              sx: {
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                },
              }
            }}
          />
        </Box>
      </Paper>

      <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          fullWidth
          label="Group Name"
          name="name"
          value={payload?.name}
          onChange={handleInputChange}
          variant="outlined"
          required
          error={!!errors.name}
          helperText={errors.name}
        />

        <FormControl fullWidth required error={!!errors.categoryId}>
          <InputLabel>Category</InputLabel>
          <Select
            name="categoryId"
            value={payload?.categoryId}
            label="Category"
            onChange={handleInputChange}
          >
            {categories?.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
          {errors.categoryId && (
            <Typography variant="caption" color="error" sx={{ mt: 1, mx: 1.75 }}>
              {errors.categoryId}
            </Typography>
          )}
        </FormControl>

        <TextField
          fullWidth
          label="Description"
          name="description"
          value={payload?.description}
          onChange={handleInputChange}
          multiline
          rows={4}
          variant="outlined"
          required
          error={!!errors.description}
          helperText={errors.description}
        />

        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          Add Members
        </Typography>

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search members..."
          value={searchTerm}
          onChange={(e) => {
            setSearchQuery(e.target.value); // Add this line to trigger patient search
          }}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "auto",
            maxHeight: "400px",
          }}
        >
          <List disablePadding>
            {isLoadingPatients ? (
              // Loading skeleton
              <>
                {[1, 2, 3].map((i) => (
                  <ListItem key={i} sx={{ py: 1.5, px: 3 }}>
                    <ListItemAvatar>
                      <Skeleton variant="circular" width={40} height={40} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Skeleton width="60%" />}
                      secondary={<Skeleton width="40%" />}
                    />
                    <Skeleton variant="rectangular" width={24} height={24} />
                  </ListItem>
                ))}
              </>
            ) : (
              [...patients, ...employees]
                ?.filter((person) =>
                  `${person?.firstName} ${person?.lastName}`
                    .toLowerCase()
                    .includes(searchQuery?.toLowerCase())
                )
                .map((person) => (
                  <ListItem
                    key={`${person.id}-${person.userRoles ? "employee" : "citizen"
                      }`}
                    secondaryAction={
                      <Checkbox
                        edge="end"
                        onChange={() => {
                          setSelectedMembers((prev) => {
                            const existing = prev.find((m) => m.id === person.id);
                            if (existing) {
                              return prev.filter((m) => m.id !== person.id);
                            }
                            return [
                              ...prev,
                              {
                                id: person.id,
                                isHealthPractitioner: isHealthPractitioner(person),
                              },
                            ];
                          });
                        }}
                        checked={selectedMembers.some((m) => m.id === person.id)}
                      />
                    }
                  >
                    <ListItemAvatar>
                      <Avatar src={person.pictureUrl || person.profilePicture}>
                        {person.firstName?.[0]}
                        {person.lastName?.[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${person.firstName} ${person.lastName}`}
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {getUserTypeDisplay(person)}
                          {person.healthCareProvider &&
                            ` • ${person.healthCareProvider.name}`}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))
            )}
          </List>
        </Paper>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={groupLoading || docUploading}
          sx={{ mt: 2 }}
        >
          {(groupLoading || docUploading)
            ? isEditing
              ? "Updating..."
              : "Creating..."
            : isEditing
              ? "Update Group"
              : "Create Group"}
        </Button>
      </Box>
    </Container>
  );
};

export default CreateGroup;
